/**
 * The Dashboard overview's windows and pace arithmetic, Meteor-free so it can
 * be unit tested. "Today" is the viewer's local day: the client sends its
 * midnight, and everything scoped to Today counts from it.
 */

export interface OverviewArg {
  /** 0 = today (from `todayStart`); N = the last N days; null = all time. */
  days: number | null;
  /** The viewer's local midnight. */
  todayStart: Date;
  /** The viewer's IANA timezone, for bucketing today's opens by hour. */
  tz: string;
}

export interface Window { from: Date | null; to: Date }

export interface OverviewWindows {
  isToday: boolean;
  current: Window;
  /** The comparison window: yesterday to the same time of day, or the same
   *  length of days before the range. Null for all time. */
  prior: Window | null;
  /** Share of the local day elapsed (Today only, else 1). */
  dayFraction: number;
}

const DAY = 86400000;

export const overviewWindows = (arg: OverviewArg, now = new Date()): OverviewWindows => {
  const todayStart = new Date(arg.todayStart);
  if (arg.days === 0) {
    const elapsed = Math.max(0, now.getTime() - todayStart.getTime());
    return {
      isToday: true,
      current: { from: todayStart, to: now },
      prior: { from: new Date(todayStart.getTime() - DAY), to: new Date(todayStart.getTime() - DAY + elapsed) },
      dayFraction: Math.min(1, Math.max(elapsed / DAY, 1 / 24)),
    };
  }
  if (!arg.days) return { isToday: false, current: { from: null, to: now }, prior: null, dayFraction: 1 };
  const from = new Date(now.getTime() - arg.days * DAY);
  return {
    isToday: false,
    current: { from, to: now },
    prior: { from: new Date(from.getTime() - arg.days * DAY), to: from },
    dayFraction: 1,
  };
};

/** Percent change, or null when the prior figure is zero (no rate to state). */
export const pctDelta = (current: number, prior: number): number | null => (prior === 0 ? null : ((current - prior) / prior) * 100);

/** "vs yesterday" / "vs prior 7d" / "all time". */
export const compareLabel = (days: number | null): string => (days === 0 ? 'vs yesterday' : days ? `vs prior ${days}d` : 'all time');
